import React, { useState } from 'react';
import useModalStore from '../../stores/modalStore';
import { epochToIso8601 } from "../../utils/formatting";
import { BACKEND_CALL_TIMEOUT, BACKEND_URL, LOCATION_NAME_TO_ID } from "../../utils/constants";
import useEventStore from "../../stores/eventStore";
import useUserStore from "../../stores/userStore";
import { EVENT_TYPE } from "../../domain";
import getFirebaseAuthToken from "../../services/fireBaseAuthenticationService";
import axios from "axios";
import { loggedAlert } from "../../services/eventService";
import LoadingSpinner from '../../LoadingSpinner';
import { AnimatePresence, motion } from 'framer-motion';

const CompedServiceModal = () => {
    const { selectedEvent } = useEventStore();
    const {
        currentUser,
        selectedLocation,
        setEmployeeEvents,
        setRequestList
    } = useUserStore();

    const show = useModalStore(state => state.showCompedServiceModal);
    const closeModal = useModalStore(state => state.closeModal);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const logCompedServiceEvent = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        const formData = new FormData(e.target);
        const value = parseFloat(formData.get("value"));
        const notes = formData.get("notes")?.trim();
        const timestamp = new Date(formData.get("timestamp")).getTime();
        const location_id = parseInt(formData.get("location_id"), 10);
        const created_by = currentUser.user_id;
        const user_getting_credit = selectedEvent?.user_id || created_by;

        if (!timestamp || isNaN(timestamp)) {
            loggedAlert("Please select a valid timestamp.", currentUser?.user_id, location_id);
            setIsSubmitting(false);
            return;
        }

        if (!value || isNaN(value) || value <= 0) {
            loggedAlert("Please enter a valid dollar value greater than 0.", currentUser?.user_id, location_id);
            setIsSubmitting(false);
            return;
        }

        if (!notes) {
            loggedAlert("Provide an explanation for the comped service.", currentUser?.user_id, location_id);
            setIsSubmitting(false);
            return;
        }

        const payload = {
            event_type: EVENT_TYPE.COMPED_SERVICE,
            event_timestamp: timestamp,
            insert_timestamp: Date.now(),
            notes,
            created_by,
            user_id: user_getting_credit,
            location_id,
            value,
            needs_review: true
        };

        try {
            const token = await getFirebaseAuthToken();

            const response = await axios.post(`${BACKEND_URL}/timesheet`, payload, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: BACKEND_CALL_TIMEOUT
            });

            if (response.status === 200) {
                const newlyAddedEvent = response.data;
                setEmployeeEvents(user_getting_credit, prev => [newlyAddedEvent, ...prev]);
                setRequestList(prev => [newlyAddedEvent, ...prev]);
                closeModal("compedServiceModal");
                e.target.reset();
            } else {
                loggedAlert(`Error: ${response.data.error}`, currentUser?.user_id, location_id);
            }
        } catch (error) {
            console.error('Error logging comped service:', error);
            const errorMessage = error?.response?.data?.details || error?.message || "Unknown error occurred";
            loggedAlert(`An error occurred while logging the comped service: ${errorMessage}`, currentUser?.user_id, location_id);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!show) return null;

    const inputClass = "mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition text-sm";

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <form onSubmit={logCompedServiceEvent} className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-800">Log Comped Service</h3>
                    <button
                        type="button"
                        onClick={() => closeModal("compedServiceModal")}
                        className="!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-100 !text-gray-400 hover:!text-gray-600 !transition-colors"
                    >
                        ✕
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <label className="block text-sm font-medium text-gray-700">
                        Date & Time
                        <input
                            className={inputClass}
                            name="timestamp"
                            required
                            type="datetime-local"
                            defaultValue={epochToIso8601(Date.now())}
                        />
                    </label>

                    <label className="block text-sm font-medium text-gray-700">
                        Dollar Value
                        <input
                            className={inputClass}
                            name="value"
                            required
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                        />
                    </label>

                    <label className="block text-sm font-medium text-gray-700">
                        Location
                        <select
                            className={inputClass}
                            name="location_id"
                            defaultValue={selectedLocation}
                            required
                        >
                            <option value="" disabled>Select a location</option>
                            {Object.entries(LOCATION_NAME_TO_ID)
                                .filter(([_, id]) => id !== null)
                                .map(([name, id]) => (
                                    <option key={id} value={id}>{name}</option>
                                ))}
                        </select>
                    </label>

                    <label className="block text-sm font-medium text-gray-700">
                        Notes
                        <textarea
                            className={`${inputClass} resize-y min-h-[60px]`}
                            name="notes"
                            required
                            placeholder="Explain the comped service..."
                        />
                    </label>
                </div>

                <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => closeModal("compedServiceModal")}
                        className="!px-4 !py-2 !text-sm !font-medium !text-gray-600 !bg-transparent !border !border-gray-200 !rounded-xl hover:!bg-gray-50 !transition-colors"
                    >
                        Cancel
                    </button>
                    <div className="w-[100px] h-[38px] relative">
                        <AnimatePresence mode="wait" initial={false}>
                            {isSubmitting ? (
                                <motion.div
                                    key="spinner"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="absolute inset-0 flex justify-center items-center"
                                >
                                    <LoadingSpinner />
                                </motion.div>
                            ) : (
                                <motion.button
                                    key="button"
                                    type="submit"
                                    className="!absolute !inset-0 !px-4 !py-2 !text-sm !font-semibold !text-white !bg-indigo-600 hover:!bg-indigo-700 !border-none !rounded-xl !shadow-sm hover:!shadow-md !transition-all"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                >
                                    Submit
                                </motion.button>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default CompedServiceModal;
